/**
 * Retention Team
 *
 * What each retention rep saved in a window — failed payments recovered, BNPL
 * renewals, upsells — the call-center work behind it, whether working a task
 * beats leaving it to the queue, and how patients answered the retention texts
 * (rep texts and the automated Customer.io → Sendblue flows).
 *
 * See backend services/retentionTeamReport for the crediting rule.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse, subDays } from 'date-fns'
import { Loader2, RefreshCw, AlertCircle, LifeBuoy, X } from 'lucide-react'

import { retentionTeamAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { formatMoney, formatCount } from '../components/DailyReportTable'

const QUEUE_LABELS = { failed_renewal: 'Failed payment', upcoming_bnpl: 'BNPL renewal', upcoming_renewal: 'Upsell' }

/** Manifold amounts are cents, as the shared formatter expects. */
const money = formatMoney

export function formatRate(value) {
  if (value === null || value === undefined) return '—'
  return `${(value * 100).toFixed(1)}%`
}

/** Minutes as "25m", "3.2h" or "2.1d". */
export function formatDuration(minutes) {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${Math.round(minutes)}m`
  if (minutes < 48 * 60) return `${(minutes / 60).toFixed(1)}h`
  return `${(minutes / 1440).toFixed(1)}d`
}

function formatWhen(ms) {
  if (!ms) return '—'
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function Kpi({ label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl sm:text-2xl font-semibold tabular-nums mt-1">{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
      </CardContent>
    </Card>
  )
}

function Sub({ children }) {
  return <span className="block text-[11px] font-normal text-muted-foreground">{children}</span>
}

const th = 'px-4 py-2 font-medium'
const thr = `${th} text-right whitespace-nowrap`
const td = 'px-4 py-2.5'
const tdr = `${td} text-right tabular-nums whitespace-nowrap`

/** Per-rep results, work and texts. Exported for the render test. */
export function RepTable({ reps, totals, selectedRep, onSelect }) {
  const row = (rep, isTotal = false) => (
    <tr
      key={rep.key}
      onClick={isTotal ? undefined : () => onSelect(rep.key === selectedRep ? '' : rep.key)}
      className={isTotal ? 'font-semibold bg-muted/30' : `border-b cursor-pointer hover:bg-muted/40 ${rep.key === selectedRep ? 'bg-muted/60' : ''}`}
    >
      <td className={td}>
        <span className="font-medium">{rep.name}</span>
        {!isTotal && <Sub>{rep.email || rep.key}</Sub>}
      </td>
      <td className={tdr}>
        {money(rep.saved_revenue)}
        <Sub>{formatCount(rep.saves)} saves{rep.exact_saves ? ` · ${formatCount(rep.exact_saves)} tracked` : ''}</Sub>
      </td>
      <td className={tdr}>{money(rep.recovered_revenue)}<Sub>{formatCount(rep.recovered)}</Sub></td>
      <td className={tdr}>{money(rep.bnpl_revenue)}<Sub>{formatCount(rep.bnpl_renewed)}</Sub></td>
      <td className={tdr}>{money(rep.renewed_revenue)}<Sub>{formatCount(rep.renewed)}</Sub></td>
      <td className={tdr}>{formatCount(rep.plan_changes)}</td>
      <td className={tdr}>{formatCount(rep.tasks_worked)}<Sub>{formatCount(rep.claims)} claimed · {formatCount(rep.replies_picked_up)} after a reply</Sub></td>
      <td className={tdr}>{formatDuration(rep.median_minutes_to_claim)}</td>
      <td className={tdr}>
        {formatCount(rep.calls)}
        <Sub>{formatCount(rep.no_answer)} no answer · {formatCount(rep.left_voicemail)} VM · {formatCount(rep.callback)} callback</Sub>
      </td>
      <td className={tdr}>{formatCount(rep.checkouts_sent)}</td>
      <td className={tdr}>{formatCount(rep.texts_sent)}<Sub>{formatCount(rep.patients_texted)} patients</Sub></td>
      <td className={tdr}>
        {formatRate(rep.response_rate)}
        <Sub>{formatCount(rep.patients_replied)} replied</Sub>
      </td>
    </tr>
  )
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className={th}>Rep</th>
            <th className={thr}>Saved</th>
            <th className={thr}>Failed payments</th>
            <th className={thr}>BNPL</th>
            <th className={thr}>Renewals</th>
            <th className={thr}>Plan changes</th>
            <th className={thr}>Tasks worked</th>
            <th className={thr} title="Median time from the patient's reply to the rep's claim">Reply → claim</th>
            <th className={thr}>Calls logged</th>
            <th className={thr}>Checkout links</th>
            <th className={thr}>Texts</th>
            <th className={thr}>Response rate</th>
          </tr>
        </thead>
        <tbody>
          {reps.map(rep => row(rep))}
          {row(totals, true)}
        </tbody>
      </table>
    </div>
  )
}

export function QueueTable({ queues, creditDays }) {
  const paidCell = (paid, of, value) => (
    <td className={tdr}>{formatRate(value)}<Sub>{formatCount(paid)} of {formatCount(of)}</Sub></td>
  )
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className={th}>Queue</th>
            <th className={thr}>Due in range</th>
            <th className={thr}>Replied to text</th>
            <th className={thr}>Rep claimed → paid</th>
            <th className={thr}>Replied, no rep → paid</th>
            <th className={thr}>No reply → paid</th>
            <th className={thr}>Paid in range</th>
            <th className={thr}>Credited to reps</th>
          </tr>
        </thead>
        <tbody>
          {queues.map(queue => (
            <tr key={queue.kind} className="border-b last:border-0">
              <td className={td}>{queue.label}</td>
              <td className={tdr}>
                {formatCount(queue.due)}
                {queue.pending > 0 && <Sub>{formatCount(queue.pending)} still open</Sub>}
              </td>
              <td className={tdr}>{formatRate(queue.reply_rate)}<Sub>{formatCount(queue.replied)} of {formatCount(queue.texted)} texted</Sub></td>
              {paidCell(queue.claimed_paid, queue.claimed, queue.claimed_rate)}
              {paidCell(queue.replied_unclaimed_paid, queue.replied_unclaimed, queue.replied_unclaimed_rate)}
              {paidCell(queue.no_reply_paid, queue.no_reply, queue.no_reply_rate)}
              <td className={tdr}>{money(queue.revenue)}<Sub>{formatCount(queue.paid)}</Sub></td>
              <td className={tdr}>{money(queue.credited_revenue)}<Sub>{formatCount(queue.credited)}</Sub></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-2 text-[11px] text-muted-foreground border-t">
        Renewal cycles due in the range, split by what happened: a rep worked it, the patient answered the automated text
        but no rep worked it, or the patient never answered. Reps claim only after a reply, so compare the first two
        columns — both groups answered — to see what a rep adds. Replies that said no are never claimed, so the
        comparison still favors reps somewhat. A cycle still unpaid less than {creditDays} days after it was due is left
        out until it settles.
      </p>
    </div>
  )
}

export function FlowTable({ flows, replyHours }) {
  if (!flows.length) return <p className="py-8 text-center text-sm text-muted-foreground">No automated retention texts in this range.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className={th}>Flow</th>
            <th className={thr}>Sent</th>
            <th className={thr}>Failed to send</th>
            <th className={thr}>Response rate</th>
            <th className={thr}>Claimed by a rep</th>
            <th className={thr}>Paid within 14 days</th>
          </tr>
        </thead>
        <tbody>
          {flows.map(flow => (
            <tr key={flow.flow} className="border-b last:border-0">
              <td className={td}>{flow.label}</td>
              <td className={tdr}>{formatCount(flow.sent)}{flow.queued > 0 && <Sub>{formatCount(flow.queued)} queued</Sub>}</td>
              <td className={tdr}>{formatCount(flow.failed)}<Sub>{formatRate(flow.delivery_failure_rate)}</Sub></td>
              <td className={tdr}>
                {formatRate(flow.response_rate)}
                <Sub>{formatCount(flow.replied)} replied{flow.reply_pending ? ` · ${formatCount(flow.reply_pending)} waiting` : ''}</Sub>
              </td>
              <td className={tdr}>{formatRate(flow.claim_rate)}<Sub>{formatCount(flow.claimed)} of the replies</Sub></td>
              <td className={tdr}>
                {formatRate(flow.paid_rate)}
                <Sub>{formatCount(flow.paid)} paid{flow.paid_pending ? ` · ${formatCount(flow.paid_pending)} waiting` : ''}</Sub>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-2 text-[11px] text-muted-foreground border-t">
        Texts Customer.io sent through the call center's Sendblue relay. A reply is the patient texting back within {replyHours} hours;
        texts sent less than {replyHours} hours ago are left out of the rate. Paid means the subscription was billed within 14 days
        (for a failed payment, that invoice was paid). The relay keeps 90 days of history.
      </p>
    </div>
  )
}

export function UnclaimedTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className={`${th} whitespace-nowrap`}>Replied</th>
            <th className={th}>Queue</th>
            <th className={th}>Patient</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.task_id} className="border-b last:border-0">
              <td className={`${td} whitespace-nowrap text-muted-foreground`}>{formatWhen(row.replied_at)}</td>
              <td className={td}>{QUEUE_LABELS[row.queue] || row.queue}</td>
              <td className={`${td} font-mono text-xs`}><span className="select-all">{row.patient_id || '—'}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-2 text-[11px] text-muted-foreground border-t">
        Patients who answered an automated retention text in the range, whose renewal is still unpaid, and whom no rep has
        worked since they answered. Open them in the call center's retention queue.
      </p>
    </div>
  )
}

export default function RetentionTeamPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentClinicId } = useAuthStore()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const selectedRep = searchParams.get('rep') || ''

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      const from = parse(startParam, 'yyyy-MM-dd', new Date())
      const to = parse(endParam, 'yyyy-MM-dd', new Date())
      if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
    }
    return { from: subDays(new Date(), 29), to: new Date() }
  }, [searchParams])

  const setDateRange = useCallback((range) => {
    if (!range?.from || !range?.to) return
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      params.set('start', format(range.from, 'yyyy-MM-dd'))
      params.set('end', format(range.to, 'yyyy-MM-dd'))
      return params
    })
  }, [setSearchParams])

  const selectRep = useCallback((key) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev)
      if (key) params.set('rep', key)
      else params.delete('rep')
      return params
    })
  }, [setSearchParams])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await retentionTeamAPI.get({
        startDate: format(dateRange.from, 'yyyy-MM-dd'),
        endDate: format(dateRange.to, 'yyyy-MM-dd'),
      })
      setData(response.data.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [dateRange])

  useEffect(() => { fetchData() }, [fetchData, currentClinicId])

  const reps = useMemo(() => data?.reps || [], [data])
  const totals = data?.totals
  const repByKey = useMemo(() => new Map(reps.map(rep => [rep.key, rep])), [reps])
  const saves = useMemo(() => (data?.saves || []).filter(save => !selectedRep || save.rep === selectedRep), [data, selectedRep])
  const selected = repByKey.get(selectedRep)
  const failedQueue = data?.queues?.find(queue => queue.kind === 'failed_renewal')
  const unclaimed = useMemo(() => data?.unclaimed_replies || [], [data])
  const flowTotals = useMemo(() => {
    const sum = field => (data?.flows || []).reduce((n, flow) => n + (flow[field] || 0), 0)
    const counted = sum('sent') - sum('reply_pending')
    return { sent: sum('sent'), replied: sum('replied'), response_rate: counted ? sum('replied') / counted : null }
  }, [data])

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">Retention Team</h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            Renewals saved from the call center's retention queues, and how patients answered retention texts
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="outline" size="icon" onClick={fetchData} className="h-8 w-8 sm:h-9 sm:w-9">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && !data.available && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted text-sm text-muted-foreground">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{data.reason}</span>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : totals ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Saved by reps" value={money(totals.saved_revenue)} hint={`${formatCount(totals.saves)} renewals · ${money(totals.recovered_revenue)} failed payments`} />
            <Kpi
              label="Text response rate"
              value={formatRate(flowTotals.response_rate)}
              hint={`${formatCount(flowTotals.replied)} of ${formatCount(flowTotals.sent)} automated texts answered`}
            />
            <Kpi
              label="Failed payments paid"
              value={failedQueue ? `${formatRate(failedQueue.claimed_rate)} vs ${formatRate(failedQueue.replied_unclaimed_rate)}` : '—'}
              hint="replied & rep claimed vs replied, no rep"
            />
            <Kpi
              label="Replies not picked up"
              value={formatCount(unclaimed.length)}
              hint="answered, unclaimed and still unpaid"
            />
            <Kpi label="Reply → claim" value={formatDuration(totals.median_minutes_to_claim)} hint={`median · ${formatCount(totals.replies_picked_up)} replies claimed`} />
          </div>

          <Card>
            <CardContent className="p-0">
              {reps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <LifeBuoy className="h-8 w-8 mb-2 opacity-40" />
                  <p className="text-sm">No rep worked a retention task in this range.</p>
                </div>
              ) : (
                <RepTable reps={reps} totals={totals} selectedRep={selectedRep} onSelect={selectRep} />
              )}
            </CardContent>
          </Card>

          <h2 className="text-sm font-semibold">Queues: what happened to each payment due</h2>
          <Card><CardContent className="p-0"><QueueTable queues={data.queues} creditDays={data.credit_days} /></CardContent></Card>

          <h2 className="text-sm font-semibold">Automated retention texts</h2>
          <Card><CardContent className="p-0"><FlowTable flows={data.flows} replyHours={data.reply_hours} /></CardContent></Card>

          {unclaimed.length > 0 && (
            <>
              <h2 className="text-sm font-semibold">
                Replies not picked up
                <span className="ml-2 font-normal text-muted-foreground">{formatCount(unclaimed.length)}</span>
              </h2>
              <Card>
                <CardContent className="p-0">
                  <UnclaimedTable rows={unclaimed} />
                </CardContent>
              </Card>
            </>
          )}

          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">
              {selected ? `${selected.name}'s saves` : 'Every save'}
              <span className="ml-2 font-normal text-muted-foreground">{formatCount(saves.length)}</span>
            </h2>
            {selected && (
              <Button variant="outline" size="sm" onClick={() => selectRep('')}>
                <X className="h-3.5 w-3.5 mr-1" /> All reps
              </Button>
            )}
          </div>
          <Card>
            <CardContent className="p-0">
              {saves.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted-foreground">No saves in this range.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b">
                        <th className={`${th} whitespace-nowrap`}>Paid</th>
                        <th className={th}>Rep</th>
                        <th className={th}>Queue</th>
                        <th className={th}>Credit</th>
                        <th className={thr}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {saves.map(save => (
                        <tr key={save.invoice_id} className="border-b last:border-0 hover:bg-muted/40">
                          <td className={`${td} whitespace-nowrap text-muted-foreground`}>{formatWhen(save.paid_at)}</td>
                          <td className={`${td} whitespace-nowrap`}>{repByKey.get(save.rep)?.name || '—'}</td>
                          <td className={td}>{QUEUE_LABELS[save.queue] || save.queue}</td>
                          <td className={`${td} text-muted-foreground`}>{save.exact ? 'Tracked link' : `Worked within ${data.credit_days} days`}</td>
                          <td className={`${tdr} font-medium`}>{money(save.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Customer.io texts each patient in a retention queue; reps claim the ones who answer. A save is a renewal from a retention queue (failed payment, BNPL renewal or upsell) paid in this range. It goes
            to the rep whose tracked checkout link or plan change Manifold recorded, or else to the last rep who worked the
            task in the call center within {data.credit_days} days before it was paid. A payment nobody worked credits nobody.
            Amounts are net of refunds. The team is everyone who worked a retention task in the range. Rep texts count only
            when sent to a patient with a retention task, and automated texts sent from a rep's line are not theirs. A
            response is a reply within {data.reply_hours} hours. Call counts are the outcomes reps logged; there is no
            phone-system call log.
          </p>
        </>
      ) : null}
    </div>
  )
}
